// SPDX-License-Identifier: AGPL-3.0-or-later

// Vector art for progression badges: glossy, softly lit emblems (one per category). The tier picks
// a material from the shared ladder; the last steps add sparkles and a halo.

import type {AchievementCategoryId} from '@app/features/user/achievements/AchievementCatalog';
import type React from 'react';
import {useId} from 'react';

interface Material {
	hi: string;
	light: string;
	mid: string;
	dark: string;
	deep: string;
}

// Bronze, Silver, Gold, Emerald, Teal, Sapphire, Amethyst, Ruby, Aurora: every step is vivid.
const LADDER: ReadonlyArray<Material> = [
	{hi: '#ffe8cf', light: '#ffb06b', mid: '#e0782f', dark: '#9a4612', deep: '#4f2006'},
	{hi: '#ffffff', light: '#eef4fb', mid: '#b9c8dc', dark: '#7186a3', deep: '#34425a'},
	{hi: '#fff8d6', light: '#ffdc5c', mid: '#f5ac14', dark: '#ad6a00', deep: '#5c3500'},
	{hi: '#e4fff1', light: '#6ef0ab', mid: '#1fbf72', dark: '#0b7a47', deep: '#053b22'},
	{hi: '#dffcff', light: '#5fe6f2', mid: '#14b2c7', dark: '#0a6f86', deep: '#05384a'},
	{hi: '#e3f1ff', light: '#7cbcff', mid: '#2f7be6', dark: '#1a4ca8', deep: '#0b2457'},
	{hi: '#f6e9ff', light: '#cf9fff', mid: '#9a57f0', dark: '#6129b8', deep: '#2f1066'},
	{hi: '#ffe3e8', light: '#ff7f96', mid: '#e8284e', dark: '#9c0f2e', deep: '#4f0515'},
	{hi: '#ffffff', light: '#9ef3ff', mid: '#a982ff', dark: '#ff6fcf', deep: '#3a2a8a'},
];

/** Rank 0..8 of a tier on the shared ladder, whatever the category's ladder length. */
export function ladderIndex(tierIndex: number, tierCount: number): number {
	if (tierCount <= 1) return LADDER.length - 1;
	return Math.round((Math.max(0, tierIndex) * (LADDER.length - 1)) / (tierCount - 1));
}

// Each category starts from its own material so the first tiers do not all look alike;
// the top rank is always Aurora.
const CATEGORY_MATERIAL_OFFSET: Record<AchievementCategoryId, number> = {
	moon: 1,
	pro: 2,
	atlas: 3,
	playtime: 5,
	crystal: 7,
};

function materialFor(categoryId: AchievementCategoryId, tierIndex: number, tierCount: number): Material {
	const rank = ladderIndex(tierIndex, tierCount);
	if (rank === LADDER.length - 1) return LADDER[rank];
	return LADDER[(rank + CATEGORY_MATERIAL_OFFSET[categoryId]) % (LADDER.length - 1)];
}

export function ladderColor(categoryId: AchievementCategoryId, tierIndex: number, tierCount: number): string {
	return materialFor(categoryId, tierIndex, tierCount).mid;
}

/** CSS custom properties for surfaces lit by a badge (hero backdrop, chips). */
export function ladderVars(categoryId: AchievementCategoryId, tierIndex: number, tierCount: number): Record<string, string> {
	const material = materialFor(categoryId, tierIndex, tierCount);
	return {'--badge-accent': material.mid, '--badge-light': material.light, '--badge-deep': material.deep};
}

interface AchievementBadgeArtProps {
	categoryId: AchievementCategoryId;
	tierIndex: number;
	tierCount: number;
	size?: number;
	className?: string;
	/** Tier not reached yet (shown with a lock mark by the caller). */
	locked?: boolean;
}

interface ArtIds {
	body: string;
	rim: string;
	shine: string;
	shadow: string;
	blur: string;
	inner: string;
	mask: string;
}

interface EmblemProps {
	m: Material;
	ids: ArtIds;
	aurora: boolean;
}

export function AchievementBadgeArt({
	categoryId,
	tierIndex,
	tierCount,
	size = 64,
	className,
	locked = false,
}: AchievementBadgeArtProps) {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
	const step = ladderIndex(tierIndex, tierCount);
	const m = materialFor(categoryId, tierIndex, tierCount);
	const aurora = step === LADDER.length - 1;
	const ids: ArtIds = {
		body: `ab${uid}`,
		rim: `ar${uid}`,
		shine: `as${uid}`,
		shadow: `ad${uid}`,
		blur: `al${uid}`,
		inner: `ai${uid}`,
		mask: `am${uid}`,
	};
	const small = size < 28;
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 64 64"
			className={className}
			aria-hidden
			data-locked={locked || undefined}
		>
			<defs>
				{aurora ? (
					<linearGradient id={ids.body} x1="0.1" y1="0" x2="0.9" y2="1">
						<stop offset="0" stopColor="#c8fbff" />
						<stop offset="0.35" stopColor="#8fd0ff" />
						<stop offset="0.65" stopColor="#b088ff" />
						<stop offset="1" stopColor="#ff6fc8" />
					</linearGradient>
				) : (
					<radialGradient id={ids.body} cx="0.32" cy="0.26" r="0.85">
						<stop offset="0" stopColor={m.light} />
						<stop offset="0.5" stopColor={m.mid} />
						<stop offset="1" stopColor={m.dark} />
					</radialGradient>
				)}
				<linearGradient id={ids.rim} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" stopColor={m.hi} stopOpacity="0.9" />
					<stop offset="0.5" stopColor={m.light} stopOpacity="0.2" />
					<stop offset="1" stopColor={m.deep} stopOpacity="0.6" />
				</linearGradient>
				<linearGradient id={ids.shine} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
					<stop offset="1" stopColor="#ffffff" stopOpacity="0" />
				</linearGradient>
				<radialGradient id={ids.inner} cx="0.5" cy="0.6" r="0.6">
					<stop offset="0" stopColor={m.hi} stopOpacity="0.95" />
					<stop offset="1" stopColor={m.light} stopOpacity="0" />
				</radialGradient>
				<filter id={ids.shadow} x="-20%" y="-20%" width="140%" height="150%">
					<feDropShadow dx="0" dy={small ? 1 : 2.2} stdDeviation={small ? 0.8 : 1.8} floodColor={m.deep} floodOpacity="0.55" />
				</filter>
				<filter id={ids.blur} x="-30%" y="-30%" width="160%" height="160%">
					<feGaussianBlur stdDeviation="1.1" />
				</filter>
			</defs>
			{aurora ? <circle cx="32" cy="33" r="29" fill="none" stroke="#bfa2ff" strokeOpacity="0.35" strokeWidth="1.5" strokeDasharray="2 4" /> : null}
			<g filter={`url(#${ids.shadow})`}>{renderEmblem(categoryId, {m, ids, aurora})}</g>
			{step >= 5 ? <Sparkles count={step >= 7 ? 3 : step >= 6 ? 2 : 1} color={aurora ? '#ffffff' : m.hi} /> : null}
		</svg>
	);
}

function renderEmblem(categoryId: AchievementCategoryId, props: EmblemProps): React.ReactNode {
	switch (categoryId) {
		case 'moon':
			return <MoonEmblem {...props} />;
		case 'pro':
			return <CrownEmblem {...props} />;
		case 'playtime':
			return <GamepadEmblem {...props} />;
		case 'atlas':
			return <CartridgeFanEmblem {...props} />;
		case 'crystal':
			return <BoostCoreEmblem {...props} />;
	}
}

/** Glossy crescent with soft craters. */
function MoonEmblem({m, ids}: EmblemProps) {
	return (
		<g>
			<mask id={ids.mask}>
				<rect width="64" height="64" fill="white" />
				<circle cx="42" cy="24" r="18" fill="black" />
			</mask>
			<g mask={`url(#${ids.mask})`}>
				<circle cx="31" cy="33" r="22" fill={`url(#${ids.body})`} />
				<circle cx="31" cy="33" r="22" fill="none" stroke={`url(#${ids.rim})`} strokeWidth="1.6" />
				<circle cx="18" cy="38" r="3.6" fill={m.dark} opacity="0.35" />
				<circle cx="18.4" cy="37.4" r="3.6" fill="none" stroke={m.hi} strokeOpacity="0.35" strokeWidth="0.8" />
				<circle cx="27" cy="48" r="2.4" fill={m.dark} opacity="0.3" />
				<circle cx="14" cy="27" r="1.7" fill={m.dark} opacity="0.3" />
				<path d="M13 30c1-7 5-12 11-15" stroke={`url(#${ids.shine})`} strokeWidth="3.4" strokeLinecap="round" fill="none" filter={`url(#${ids.blur})`} />
			</g>
			<path d="M45 12.5l1.2 2.6 2.6 1.2-2.6 1.2-1.2 2.6-1.2-2.6-2.6-1.2 2.6-1.2Z" fill={m.hi} opacity="0.95" />
		</g>
	);
}

/** Jewelled crown: the Pro emblem. */
function CrownEmblem({m, ids}: EmblemProps) {
	const crown = 'M10 22l11 9.5L32 13l11 18.5L54 22l-4.6 24H14.6Z';
	return (
		<g>
			<path d={crown} fill={`url(#${ids.body})`} />
			<path d="M32 13l11 18.5L54 22l-4.6 24H32Z" fill={m.deep} opacity="0.18" />
			<path d={crown} fill="none" stroke={`url(#${ids.rim})`} strokeWidth="1.6" strokeLinejoin="round" />
			<path d="M14 25.5 21 31.5" stroke="#ffffff" strokeOpacity="0.6" strokeWidth="1.6" strokeLinecap="round" />
			<path d="M23 29.5 31 16.5" stroke="#ffffff" strokeOpacity="0.7" strokeWidth="2" strokeLinecap="round" filter={`url(#${ids.blur})`} />
			<rect x="12.5" y="44" width="39" height="9" rx="3.2" fill={`url(#${ids.body})`} />
			<rect x="12.5" y="44" width="39" height="9" rx="3.2" fill={m.deep} opacity="0.22" />
			<rect x="12.5" y="44" width="39" height="9" rx="3.2" fill="none" stroke={`url(#${ids.rim})`} strokeWidth="1.3" />
			<rect x="15" y="45.4" width="22" height="1.8" rx="0.9" fill="#ffffff" opacity="0.55" />
			{[
				[10, 22],
				[32, 13],
				[54, 22],
			].map(([x, y]) => (
				<g key={x}>
					<circle cx={x} cy={y} r="3.6" fill="#ffffff" />
					<circle cx={x} cy={y} r="3.6" fill={m.light} opacity="0.35" />
					<circle cx={x - 1.1} cy={y - 1.1} r="1.2" fill="#ffffff" />
				</g>
			))}
			<path d="M32 33.5 36.5 39 32 44.5 27.5 39Z" fill="#ff4f73" />
			<path d="M32 33.5 36.5 39H27.5Z" fill="#ff9fb2" />
			<circle cx="21" cy="48.5" r="2" fill="#5ad1ff" />
			<circle cx="43" cy="48.5" r="2" fill="#5ad1ff" />
			<circle cx="32" cy="48.5" r="2.4" fill="#ffd34d" />
		</g>
	);
}

/** Glossy gamepad with a small stopwatch on top. */
function GamepadEmblem({m, ids}: EmblemProps) {
	const body =
		'M17 21h30c6.2 0 10 4.4 11.4 11.6l2 10.4c.9 4.8-2.2 8.4-6.4 8.4-3 0-5-1.6-7-4.4l-2.6-3.6H19.6L17 47c-2 2.8-4 4.4-7 4.4-4.2 0-7.3-3.6-6.4-8.4l2-10.4C7 25.4 10.8 21 17 21Z';
	return (
		<g>
			<circle cx="32" cy="13" r="7.5" fill={`url(#${ids.body})`} />
			<circle cx="32" cy="13" r="7.5" fill="none" stroke={`url(#${ids.rim})`} strokeWidth="1.2" />
			<circle cx="32" cy="13" r="5" fill={m.deep} />
			<path d="M32 9.6V13l2.4 1.6" stroke={m.hi} strokeWidth="1.3" strokeLinecap="round" fill="none" />
			<rect x="30.4" y="3.6" width="3.2" height="2.6" rx="1" fill={m.light} />
			<path d={body} fill={`url(#${ids.body})`} />
			<path d={body} fill="none" stroke={`url(#${ids.rim})`} strokeWidth="1.5" />
			<path d="M18 24.5h28" stroke="#ffffff" strokeOpacity="0.55" strokeWidth="2.2" strokeLinecap="round" filter={`url(#${ids.blur})`} />
			<rect x="15.2" y="29.4" width="3.6" height="10.4" rx="1.1" fill="#ffffff" fillOpacity="0.92" />
			<rect x="11.8" y="32.8" width="10.4" height="3.6" rx="1.1" fill="#ffffff" fillOpacity="0.92" />
			<circle cx="46" cy="30.6" r="2.4" fill="#ffd34d" />
			<circle cx="50.6" cy="35" r="2.4" fill="#ff5a6e" />
			<circle cx="41.4" cy="35" r="2.4" fill="#4fd18b" />
			<circle cx="46" cy="39.4" r="2.4" fill="#5aa7ff" />
			<circle cx="26" cy="41" r="3" fill={m.deep} opacity="0.55" />
			<circle cx="38" cy="41" r="3" fill={m.deep} opacity="0.55" />
		</g>
	);
}

/** Three game cartridges fanned out like a hand of cards. */
function CartridgeFanEmblem({m, ids}: EmblemProps) {
	const cartridge = (rotate: number, shade: number, key: string) => (
		<g key={key} transform={`rotate(${rotate} 32 52)`}>
			<path d="M20 12a3 3 0 0 1 3-3h18a3 3 0 0 1 3 3v35a3 3 0 0 1-3 3H23a3 3 0 0 1-3-3Z" fill={`url(#${ids.body})`} />
			<path d="M20 12a3 3 0 0 1 3-3h18a3 3 0 0 1 3 3v35a3 3 0 0 1-3 3H23a3 3 0 0 1-3-3Z" fill={m.deep} opacity={shade} />
			<path d="M20 12a3 3 0 0 1 3-3h18a3 3 0 0 1 3 3v35a3 3 0 0 1-3 3H23a3 3 0 0 1-3-3Z" fill="none" stroke={`url(#${ids.rim})`} strokeWidth="1.2" />
			<rect x="24" y="16" width="16" height="17" rx="2" fill="#ffffff" fillOpacity="0.9" />
			<rect x="24" y="16" width="16" height="17" rx="2" fill={m.light} fillOpacity="0.35" />
			<path d="M27.5 27.5l3.2-5 2.4 3.2 1.6-2 2.8 3.8Z" fill={m.dark} opacity="0.8" />
			<circle cx="35.8" cy="20.6" r="1.6" fill={m.dark} opacity="0.7" />
			{[0, 1, 2, 3].map((i) => (
				<rect key={i} x={24.5 + i * 4} y="40" width="2.2" height="6" rx="0.8" fill={m.deep} opacity="0.45" />
			))}
		</g>
	);
	return (
		<g>
			{cartridge(-18, 0.35, 'left')}
			{cartridge(18, 0.25, 'right')}
			{cartridge(0, 0, 'front')}
			<path d="M24 11.5h9" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.8" filter={`url(#${ids.blur})`} />
		</g>
	);
}

/** Faceted heart gem inside a tilted orbit with a riding star: support that keeps a community going. */
function BoostCoreEmblem({m, ids}: EmblemProps) {
	const heart = 'M32 54C19 45 9.5 36.5 9.5 25.5 9.5 17.4 15.6 11.5 23 11.5c4 0 7 1.8 9 4.8 2-3 5-4.8 9-4.8 7.4 0 13.5 5.9 13.5 14 0 11-9.5 19.5-22.5 28.5Z';
	return (
		<g>
			<path d="M4 34A28 10.5 0 0 1 60 34" fill="none" stroke={m.light} strokeOpacity="0.55" strokeWidth="2.2" transform="rotate(-18 32 34)" />
			<path d={heart} fill={`url(#${ids.body})`} />
			<path d="M32 16.3 32 54C19 45 9.5 36.5 9.5 25.5c0-3.6 1.2-6.8 3.3-9.2Z" fill="#ffffff" opacity="0.12" />
			<path d="M32 16.3 44 26 32 54Z" fill={m.deep} opacity="0.22" />
			<path d="M32 16.3 20 26 32 54Z" fill={m.hi} opacity="0.18" />
			<path d="M9.5 25.5 20 26 32 16.3M54.5 25.5 44 26 32 16.3M20 26h24" stroke={m.hi} strokeOpacity="0.45" strokeWidth="0.9" fill="none" />
			<path d={heart} fill="none" stroke={`url(#${ids.rim})`} strokeWidth="1.6" />
			<path d="M15 21.5c1.4-3.6 4.4-5.8 8-6.2" stroke="#ffffff" strokeWidth="2.6" strokeLinecap="round" fill="none" opacity="0.9" filter={`url(#${ids.blur})`} />
			<path d="M4 34A28 10.5 0 0 0 60 34" fill="none" stroke={m.hi} strokeWidth="2.6" strokeLinecap="round" transform="rotate(-18 32 34)" />
			<circle cx="9.4" cy="45.6" r="2.6" fill="#ffffff" />
			<path d="M52.5 17.5l1.6 3.4 3.4 1.6-3.4 1.6-1.6 3.4-1.6-3.4-3.4-1.6 3.4-1.6Z" fill="#ffffff" />
		</g>
	);
}

function Sparkles({count, color}: {count: number; color: string}) {
	const star = (x: number, y: number, r: number) =>
		`M${x} ${y - r}Q${x + r * 0.16} ${y - r * 0.16} ${x + r} ${y}Q${x + r * 0.16} ${y + r * 0.16} ${x} ${y + r}Q${x - r * 0.16} ${y + r * 0.16} ${x - r} ${y}Q${x - r * 0.16} ${y - r * 0.16} ${x} ${y - r}Z`;
	const spots: Array<[number, number, number]> = [
		[54, 11, 5],
		[9, 50, 3.6],
		[56, 52, 2.8],
	];
	return (
		<g fill={color}>
			{spots.slice(0, count).map(([x, y, r]) => (
				<path key={`${x}-${y}`} d={star(x, y, r)} />
			))}
		</g>
	);
}
